import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// When true, refresh purchase-limit metafield from the server before ATC validation.
// Kept false for snappier limit feedback; set true to re-enable live admin values.
const OFFHOO_REFRESH_PURCHASE_LIMIT_BEFORE_ATC = false;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    // Offhoo: optional theme setting — skip fly-to-cart on mobile (≤749px),
    // except product pages. App PDP flies to the floating cart; PC / H5 fly to the header cart.
    const isProductPage =
      document.body?.classList.contains('template-product') ||
      document.querySelector('[data-offhoo-pdp-floating-nav]') != null;

    if (
      !isProductPage &&
      document.body?.dataset.hideMobileFlyToCart === 'true' &&
      window.matchMedia('(max-width: 749px)').matches
    ) {
      return;
    }

    const { addToCartButton } = this.refs;
    const floatingCart = document.querySelector('.offhoo-pdp-floating-nav__cart');
    const headerCart = document.querySelector('.header-actions__cart-icon');
    // Floating cart is in the DOM on every PDP, but only visible in the App (≤749px).
    // PC / H5 use the theme default: fly to the header cart icon.
    const cartIcon =
      floatingCart instanceof HTMLElement && floatingCart.getClientRects().length > 0
        ? floatingCart
        : headerCart;

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = /** @type {HTMLElement} */ (cartIcon);

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The buy-now / accelerated checkout button container element.
 * @property {HTMLButtonElement | undefined} buyNowButton - Custom buy-now button (add to cart then go to checkout).
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });

    // Buy-now disables the button before navigating; browser back (bfcache) can
    // restore this page with the button still disabled. Re-enable on pageshow.
    window.addEventListener('pageshow', this.#onPageShow, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    // Fallback for pages without cart-items-component (e.g. page-based cart on product pages)
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const cartItem = cart.items.find(
      /** @param {any} item */
      (item) => item.variant_id.toString() === variantIdInput.toString()
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    this.dataset.cartQuantity = cartQty.toString();

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;

        if (detail?.items) {
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /** @type {AbortController | null} */
  #purchaseLimitAbortController = null;

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    void this.#processAddToCart(undefined, undefined, event);
  }

  /** @type {boolean} */
  #buyNowInProgress = false;

  /**
   * Restores buy-now after bfcache restore / quick browser back.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted && !this.#buyNowInProgress) return;
    this.#resetBuyNowState();
  };

  #resetBuyNowState() {
    this.#buyNowInProgress = false;
    const { buyNowButton, acceleratedCheckoutButtonContainer } = this.refs;
    if (!buyNowButton || !document.body.contains(buyNowButton)) return;
    const containerHidden = acceleratedCheckoutButtonContainer?.hasAttribute('hidden');
    buyNowButton.disabled = Boolean(containerHidden);
  }

  /**
   * Custom buy-now via Storefront API cartCreate: opens checkout for only the
   * current variant/qty without touching the Online Store Ajax cart session.
   * @param {Event} [event]
   */
  handleBuyNow = async (event) => {
    event?.preventDefault?.();
    if (this.#buyNowInProgress || this.#variantChangeInProgress) return;

    const { buyNowButton, addToCartTextError } = this.refs;
    if (buyNowButton?.disabled) return;

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    const form = this.querySelector('form');
    if (!form) return;

    if (OFFHOO_REFRESH_PURCHASE_LIMIT_BEFORE_ATC) {
      await this.ensurePurchaseLimitFresh();
    }

    const formData = new FormData(form);
    /** @type {Array<{variantId: string, quantity: number}>} */
    let lines = [];
    const bogoOptions = this.querySelector('offhoo-bogo-options');

    if (bogoOptions && typeof bogoOptions.getCheckoutLines === 'function') {
      const selection = bogoOptions.getCheckoutLines();
      if (!selection.lines.length) {
        const message = selection.error || 'Unable to start checkout';
        this.#showBuyNowError(message);
        return;
      }
      lines = selection.lines;
    } else {
      const variantId = String(formData.get('id') || this.#getIntendedVariantId() || '');
      const quantity = Number(formData.get('quantity')) || this.#getQuantity();
      if (!variantId || quantity < 1) return;
      lines = [{ variantId, quantity }];
    }

    const paidQuantity = lines.reduce((sum, line) => {
      const currentId = String(formData.get('id') || '');
      return line.variantId === currentId ? sum + line.quantity : sum;
    }, 0);

    // Buy-now checks out these lines alone — validate against purchase limit for
    // the paid variant only (do not combine with items already in the cart).
    const purchaseLimit = Number(this.dataset.purchaseLimit);
    if (!Number.isNaN(purchaseLimit) && purchaseLimit > 0 && paidQuantity > purchaseLimit) {
      this.displayQuantityLimitError({ maxQuantity: purchaseLimit, isPurchaseLimit: true });
      return;
    }

    if (!bogoOptions) {
      const quantityInput = this.refs.quantitySelector?.refs?.quantityInput;
      const quantity = lines[0]?.quantity || 0;
      if (quantityInput instanceof HTMLInputElement) {
        const min = Number(quantityInput.min) || 1;
        const max = quantityInput.max !== '' ? Number(quantityInput.max) : NaN;
        if (quantity < min || (!Number.isNaN(max) && quantity > max)) {
          this.displayQuantityLimitError({
            maxQuantity: Number.isNaN(max) ? null : max,
            isPurchaseLimit: false,
          });
          return;
        }
      }
    }

    this.#buyNowInProgress = true;
    if (buyNowButton) buyNowButton.disabled = true;

    try {
      const checkoutUrl = await this.#createStorefrontCheckoutUrl(lines, formData);
      window.location.assign(checkoutUrl);
    } catch (error) {
      console.error('[buy-now]', error);
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Unable to start checkout';

      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = message;
        } else {
          addToCartTextError.appendChild(document.createTextNode(message));
        }
        this.#setLiveRegionText(message);

        if (this.#timeout) clearTimeout(this.#timeout);
        this.#timeout = setTimeout(() => {
          addToCartTextError.classList.add('hidden');
          this.#clearLiveRegionText();
        }, ERROR_MESSAGE_DISPLAY_DURATION);
      }

      this.#resetBuyNowState();
    }
  };

  /**
   * @param {string} message
   */
  #showBuyNowError(message) {
    const { addToCartTextError } = this.refs;
    if (!addToCartTextError) return;
    addToCartTextError.classList.remove('hidden');
    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = message;
    } else {
      addToCartTextError.appendChild(document.createTextNode(message));
    }
    this.#setLiveRegionText(message);
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      addToCartTextError.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Creates an isolated Storefront cart and returns its checkoutUrl.
   * Leaves the theme Ajax cart (/cart.js) unchanged.
   * Line item properties and selling plans apply to the first line only.
   * @param {Array<{variantId: string, quantity: number}>} lines
   * @param {FormData} formData
   * @returns {Promise<string>}
   */
  async #createStorefrontCheckoutUrl(lines, formData) {
    const accessToken = Theme.storefront?.accessToken?.trim();
    if (!accessToken) {
      throw new Error(
        'Missing Storefront API token. Add it in Theme settings → Off-hoo Phase 1 → Storefront API token (Buy now).'
      );
    }

    const apiVersion = Theme.storefront?.apiVersion || '2025-01';
    const shop = window.Shopify?.shop;
    if (!shop) throw new Error('Shop domain unavailable');

    /** @type {{ merchandiseId: string, quantity: number, attributes?: { key: string, value: string }[], sellingPlanId?: string }[]} */
    const cartLines = lines.map((item) => ({
      merchandiseId: `gid://shopify/ProductVariant/${item.variantId}`,
      quantity: item.quantity,
    }));

    /** @type {{ key: string, value: string }[]} */
    const attributes = [];
    for (const [key, value] of formData.entries()) {
      const match = /^properties\[([^\]]+)\]$/.exec(key);
      if (!match || typeof value !== 'string' || value === '') continue;
      const propertyKey = match[1];
      if (!propertyKey) continue;
      attributes.push({ key: propertyKey, value });
    }
    if (attributes.length && cartLines[0]) cartLines[0].attributes = attributes;

    const sellingPlan = formData.get('selling_plan');
    if (typeof sellingPlan === 'string' && sellingPlan !== '' && cartLines[0]) {
      cartLines[0].sellingPlanId = `gid://shopify/SellingPlan/${sellingPlan}`;
    }

    /** @type {Record<string, unknown>} */
    const input = { lines: cartLines };
    if (window.Shopify?.country) {
      input.buyerIdentity = { countryCode: window.Shopify.country };
    }

    // Cart-level attributes → order note_attributes (Additional details).
    // Must be on CartInput.attributes, not line properties — Buy Now creates a
    // fresh Storefront cart that does not inherit Ajax /cart attributes.
    const channel =
      typeof window.OffhooApp?.getClientChannel === 'function'
        ? window.OffhooApp.getClientChannel()
        : null;
    if (
      channel === 'app' ||
      channel === 'ios' ||
      channel === 'android' ||
      channel === 'h5' ||
      channel === 'pc'
    ) {
      /** @type {{ key: string, value: string }[]} */
      const orderAttrs = [
        { key: 'client_channel', value: channel },
        { key: 'client_channel_at', value: new Date().toISOString() },
      ];
      const visitorId =
        typeof window.OffhooApp?.getOrderVisitorId === 'function'
          ? String(window.OffhooApp.getOrderVisitorId() || '').trim()
          : '';
      if (visitorId) orderAttrs.push({ key: 'visitor_id', value: visitorId });
      const deviceId =
        typeof window.OffhooApp?.getOrderDeviceId === 'function'
          ? String(window.OffhooApp.getOrderDeviceId() || '').trim()
          : '';
      if (deviceId) orderAttrs.push({ key: 'device_id', value: deviceId });
      input.attributes = orderAttrs;
    }

    const response = await fetch(`https://${shop}/api/${apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': accessToken,
      },
      body: JSON.stringify({
        query: `mutation cartCreate($input: CartInput!) {
          cartCreate(input: $input) {
            cart { checkoutUrl }
            userErrors { field message }
          }
        }`,
        variables: { input },
      }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API error (${response.status})`);
    }

    const json = await response.json();
    const payload = json?.data?.cartCreate;
    const userError = payload?.userErrors?.[0]?.message;
    if (userError) throw new Error(userError);

    const graphError = json?.errors?.[0]?.message;
    if (graphError) throw new Error(graphError);

    const checkoutUrl = payload?.cart?.checkoutUrl;
    if (!checkoutUrl) throw new Error('Unable to create checkout');

    return checkoutUrl;
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * Fetches live purchase-limit + cart qty for the current variant and applies them
   * to the quantity input / form dataset so ATC uses fresh admin values.
   * Currently gated by OFFHOO_REFRESH_PURCHASE_LIMIT_BEFORE_ATC (default false).
   * @param {string} [overrideVariantId]
   * @returns {Promise<void>}
   */
  async ensurePurchaseLimitFresh(overrideVariantId) {
    const productUrl = this.dataset.productUrl;
    const variantId = overrideVariantId || this.#getIntendedVariantId();
    if (!productUrl || !variantId) return;

    this.#purchaseLimitAbortController?.abort();
    const abortController = new AbortController();
    this.#purchaseLimitAbortController = abortController;

    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('view', 'offhoo-purchase-limit');
      url.searchParams.set('variant', variantId);

      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        // Do not send Accept: application/json — Shopify may return /products/*.js
        // style product JSON instead of rendering ?view=offhoo-purchase-limit.
        headers: { Accept: 'text/html, application/xhtml+xml' },
        signal: abortController.signal,
      });

      if (!response.ok) return;

      const text = (await response.text()).trim();
      // Some theme proxies wrap output; extract the first JSON object if needed.
      const jsonText = text.startsWith('{') ? text : text.match(/\{[\s\S]*\}/)?.[0];
      if (!jsonText) {
        console.warn('[product-form] Purchase-limit response was not JSON');
        return;
      }

      /** @type {{ variantId?: number|string, purchaseLimit?: number|null, effectiveMax?: number|null, cartQuantity?: number, product?: unknown }} */
      let data;
      try {
        data = JSON.parse(jsonText);
      } catch {
        console.warn('[product-form] Failed to parse purchase-limit response');
        return;
      }

      // Wrong payload (Ajax product JSON) — view template did not render
      if (data.product || data.variantId == null) {
        console.warn('[product-form] Unexpected purchase-limit payload; view template may not have rendered');
        return;
      }

      if (String(data.variantId) !== String(variantId)) return;

      this.#applyPurchaseLimitState({
        purchaseLimit: data.purchaseLimit ?? null,
        effectiveMax: data.effectiveMax ?? null,
        cartQuantity: Number(data.cartQuantity) || 0,
      });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.warn('[product-form] Purchase-limit refresh failed:', error);
    }
  }

  /**
   * @param {{ purchaseLimit: number|null, effectiveMax: number|null, cartQuantity: number }} state
   */
  #applyPurchaseLimitState(state) {
    const { purchaseLimit, effectiveMax, cartQuantity } = state;
    const quantitySelector = this.refs.quantitySelector;
    const quantityInput = quantitySelector?.refs?.quantityInput;

    this.dataset.cartQuantity = String(cartQuantity);

    if (purchaseLimit != null && !Number.isNaN(Number(purchaseLimit))) {
      this.dataset.purchaseLimit = String(purchaseLimit);
    } else {
      delete this.dataset.purchaseLimit;
    }

    if (!(quantityInput instanceof HTMLInputElement)) return;

    if (purchaseLimit != null && !Number.isNaN(Number(purchaseLimit))) {
      quantityInput.setAttribute('data-purchase-limit', String(purchaseLimit));
    } else {
      quantityInput.removeAttribute('data-purchase-limit');
    }

    const currentMin = quantityInput.min || '1';
    const currentStep = quantityInput.step || '1';
    const nextMax =
      effectiveMax != null && !Number.isNaN(Number(effectiveMax)) ? String(effectiveMax) : null;

    if (typeof quantitySelector?.updateConstraints === 'function') {
      quantitySelector.updateConstraints(currentMin, nextMax, currentStep);
    } else if (nextMax) {
      quantityInput.max = nextMax;
    } else {
      quantityInput.removeAttribute('max');
    }

    if (typeof quantitySelector?.setCartQuantity === 'function') {
      quantitySelector.setCartQuantity(cartQuantity);
    } else {
      quantityInput.setAttribute('data-cart-quantity', String(cartQuantity));
    }
  }

  /**
   * Syncs purchase-limit and per-variant cart quantity from freshly rendered HTML.
   * Must run on variant change — otherwise the previous variant's cart qty stays on
   * the input and falsely blocks add-to-cart for the newly selected variant.
   * @param {HTMLInputElement} sourceInput
   */
  #syncQuantityInputLimits(sourceInput) {
    const quantitySelector = this.refs.quantitySelector;
    const currentInput = quantitySelector?.refs?.quantityInput;
    if (!(currentInput instanceof HTMLInputElement)) return;

    const purchaseLimit = sourceInput.getAttribute('data-purchase-limit');
    if (purchaseLimit) {
      currentInput.setAttribute('data-purchase-limit', purchaseLimit);
      this.dataset.purchaseLimit = purchaseLimit;
    } else {
      currentInput.removeAttribute('data-purchase-limit');
      delete this.dataset.purchaseLimit;
    }

    const cartQuantity = sourceInput.getAttribute('data-cart-quantity') || '0';
    this.dataset.cartQuantity = cartQuantity;
    if (typeof quantitySelector.setCartQuantity === 'function') {
      quantitySelector.setCartQuantity(Number(cartQuantity) || 0);
    } else {
      currentInput.setAttribute('data-cart-quantity', cartQuantity);
    }
  }

  /**
   * Shows the max-quantity / purchase-limit error on the product form.
   * @param {{ maxQuantity?: number|null, isPurchaseLimit?: boolean }} validation
   */
  displayQuantityLimitError(validation) {
    const { addToCartTextError } = this.refs;
    const errorTemplate = validation.isPurchaseLimit
      ? this.dataset.purchaseLimitError || this.dataset.quantityErrorMax || ''
      : this.dataset.quantityErrorMax || '';
    const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');

    if (!addToCartTextError || !errorMessage) return;

    addToCartTextError.classList.remove('hidden');

    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = errorMessage;
    } else {
      addToCartTextError.appendChild(document.createTextNode(errorMessage));
    }

    this.#setLiveRegionText(errorMessage);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      addToCartTextError.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  async #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    // Optional live refresh (OFFHOO_REFRESH_PURCHASE_LIMIT_BEFORE_ATC). Method kept for easy re-enable.
    if (OFFHOO_REFRESH_PURCHASE_LIMIT_BEFORE_ATC) {
      await this.ensurePurchaseLimitFresh(overrideVariantId);
    }

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        this.displayQuantityLimitError(validation);

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    } else if (!overrideVariantId) {
      // Quick-add and other forms without a quantity selector
      const purchaseLimit = Number(this.dataset.purchaseLimit);
      if (!Number.isNaN(purchaseLimit) && purchaseLimit > 0) {
        const cartQuantity = Number(this.dataset.cartQuantity) || 0;
        const quantityToAdd = overrideQuantity ?? this.#getQuantity();
        if (cartQuantity + quantityToAdd > purchaseLimit) {
          for (const container of allAddToCartContainers) {
            container.disable();
          }

          this.displayQuantityLimitError({ maxQuantity: purchaseLimit, isPurchaseLimit: true });

          setTimeout(() => {
            for (const container of allAddToCartContainers) {
              container.enable();
            }
          }, ERROR_BUTTON_REENABLE_DELAY);

          return;
        }
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: /** @type {string} */ (formData.get('id')),
            quantity: itemCount,
          },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#refreshCart()
            .then((ajaxCart) => {
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                  sections: response.sections,
                  didError: false,
                },
              });

              if (this.#getVariantIdInput()) {
                this.#updateCartQuantity(ajaxCart);
              }

              return ajaxCart;
            })
            .catch(deferredEventPromise.reject);
        }
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount: totalQuantity,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(response.message));
          }
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'product-form-component',
            sourceId: this.id.toString(),
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
            didError: false,
          },
        });
        this.#updateCartQuantity(cart);
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    // Track generation to prevent a stale (aborted) call from clearing the flag
    // while a newer variant selection is still pending.
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;

    try {
      const { detail } = await event.promise;
      if (!detail?.html) return;

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';
      this.querySelector('offhoo-bogo-options')?.syncPrices?.();

      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
        this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      // Update the button state
      if (resource == null || resource.available == false) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }

      if (acceleratedCheckoutButtonContainer) {
        const unavailable = resource == null || resource.available == false;
        if (unavailable) {
          acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer.removeAttribute('hidden');
        }
        const buyNowButton = this.refs.buyNowButton;
        if (buyNowButton) {
          buyNowButton.disabled = unavailable;
        }
      }

      // Set the data attribute for the product variant media if it exists
      if (resource) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia + '&width=100'
          );
        }
      }

      // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      // Update quantity selector's min/max/step attributes and cart quantity for the new variant
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        this.#syncQuantityInputLimits(newQuantityInput);
        // Keep data-quantity-default attribute in sync with new variant's minimum quantity
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        // Store quantity value before morphing entire container
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          // Get the NEW quantity selector after morphing and update its constraints
          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            // Temporarily set the old value so updateConstraints can snap it properly
            this.refs.quantitySelector.setValue(currentQuantityValue);
            // updateConstraints will snap to valid increment if needed
            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step
            );
            this.#syncQuantityInputLimits(newQuantityInputElement);
            // Keep data-quantity-default attribute in sync with new variant's minimum quantity
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }

      // Morph volume pricing if it exists
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) return;

      // Fetch and update cart quantity for the new variant
      this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
    } finally {
      // Only clear the flag if no newer variant selection has started
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;

        // Drain any queued add-to-cart requests that accumulated during the variant change
        if (this.#addToCartQueue.length > 0) {
          const queuedItems = [...this.#addToCartQueue];
          this.#addToCartQueue = [];
          this.#processBatchAddToCart(queuedItems);
        }
      }
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
